import React, { useEffect, useState, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { Card, CardContent, CardHeader, CardTitle } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Button } from "@shadcn-in-nx/ui/button";
import { Building, Users, Coins, Loader2, RefreshCw } from "lucide-react";
import DeleteCompanyButton from "./DeleteCompanyButton";

export default function CompanyDetails({ companyId, onCompanyDeleted }) {
    const [company, setCompany] = useState(null);
    const { call: fetchDetails, isLoading } = useBluelagoon({ method: "getCompanyDetails" });

    const loadDetails = useCallback(() => {
        if (!companyId) return;
        fetchDetails({ companyId }).then(response => {
            if (response?.data) setCompany(response.data);
        }).catch(err => {
            console.error("Failed to fetch company details:", err);
        });
    }, [companyId, fetchDetails]);

    useEffect(() => {
        loadDetails();
    }, [loadDetails]);

    if (isLoading && !company) {
        return (
            <Card><CardContent className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </CardContent></Card>
        );
    }

    if (!company) return null;

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2"><Building className="h-5 w-5" />{company.name}</CardTitle>
                <Button variant="ghost" size="icon" onClick={loadDetails} disabled={isLoading}>
                    <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
                </Button>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Current Credits</p>
                        <p className="text-2xl font-semibold flex items-center gap-2">
                            <Coins className="h-5 w-5 text-yellow-500" />{company.currentCredits?.toFixed(2) || "0.00"}
                        </p>
                    </div>
                    <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Users</p>
                        <p className="text-2xl font-semibold flex items-center gap-2">
                            <Users className="h-5 w-5 text-blue-500" />{company.userCount || 0}
                        </p>
                    </div>
                </div>
                <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Subscription</p>
                    <Badge variant={company.subscriptionStatus === "ACTIVE" ? "default" : "secondary"}>
                        {company.subscriptionStatus || "No subscription"}
                    </Badge>
                </div>
                {company.users?.length > 0 && (
                    <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">Users</p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                            {company.users.map((user) => (
                                <div key={user.id} className="text-sm flex items-center justify-between py-1 border-b last:border-0">
                                    <span>{user.email}</span>
                                    <Badge variant="outline" className="text-xs">{user.role}</Badge>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <DeleteCompanyButton
                    companyId={company.id}
                    companyName={company.name}
                    subscriptionStatus={company.subscriptionStatus}
                    userCount={company.userCount}
                    onDeleted={onCompanyDeleted}
                />
            </CardContent>
        </Card>
    );
}
